import queryString from 'query-string';
import { RuleGroup } from '@experiment-management-shared/types';
import api from '@shared/api';
import { BASE_API_URL } from '@/constants/configConstants';

export type AggregationTableColumn = {
  kind?: string;
  keyName: string;
  source: string;
};

export type GetGroupByAggregationsArgs = {
  projectId: string;
  rulesTree: RuleGroup;
  groupByColumns: AggregationTableColumn[];
  tableColumns: AggregationTableColumn[];
};

export type AggregationEntity = {
  aggregatedNumLast: number;
  aggregatedNumMax: number;
  aggregatedNumMin: number;
  experimentsCount: number;
  keyName: string;
};

export type AggregationDto = {
  aggregations: AggregationEntity[];
  children: AggregationDto[];
  groupColumn: string;
  groupNameValue: string;
  kind: string;
  source: string;
  path?: { value: string; column: string; kind: string }[];
};

export type GroupByAggregationsSuccessResponse = {
  metrics: string[];
  validationMessages: null;
  rootDto: AggregationDto | null;
  message?: string;
};

export interface ExperimentMetricNamesResponse {
  metrics: string[];
}
export interface ExperimentImageNamesResponse {
  imageNames: string[];
}
export type ImageInfoDetails = {
  imageId: string;
  experimentKey: string;
  figName: string;
  imagePath: string;
  thumbnailPath: string;
  createdAt: number;
  assetSize: number;
  step: number | null;
};
export type AssetInfoDetails = {
  assetId: string;
  experimentKey: string;
  userFileName: string;
  createdAt: number;
  assetSize: number;
  step: number | null;
};
export interface AutogeneratedImagePanelDetails {
  imageInfoList: ImageInfoDetails[];
}
export interface ExperimentDataframesNamesResponse {
  dataFrameNames: string[];
}
export interface ExperimentCurveNamesResponse {
  curveNames: string[];
}
export interface AutogeneratedCurvePanelDetails {
  curveDetailsList: AssetInfoDetails[];
}

interface ExperimentPanelsAvailabilityResponse {
  experimentKey: string;
  data: {
    assets: boolean;
    audio: boolean;
    code: boolean;
    confusionMatrix: boolean;
    graph: boolean;
    histogram: boolean;
    html: boolean;
    imagesVideos: boolean;
    installedPackages: boolean;
    logOther: boolean;
    metrics: boolean;
    notes: boolean;
    outputDetails: boolean;
    panels: boolean;
    params: boolean;
    systemTab: boolean;
    textSample: boolean;
  };
}

const experimentApi = {
  fetchLatestExperiment() {
    const url = `data-fetch/get/latest-experiment`;
    const options = {
      withCredentials: true
    };
    return api.get(url, options);
  },

  fetchExperimentModels(experimentKey: string, signal: AbortSignal) {
    const url = `experiment/model?experimentKey=${experimentKey}`;
    return api.get(url, { signal });
  },

  getExperimentNameByExperimentKey(experimentKey: string) {
    const url = `experiment-name/names`;
    return api.post(url, JSON.stringify({ experimentKeys: [experimentKey] }));
  },

  getGroupByAggregations({
    projectId,
    rulesTree,
    groupByColumns = [],
    tableColumns = []
  }: GetGroupByAggregationsArgs) {
    const url = `query/experiments/aggregate`;
    const body = {
      projectId,
      rulesTree,
      groupByColumns,
      tableColumns
    };

    return api.post<GroupByAggregationsSuccessResponse>(url, body);
  },

  getRedirectURL(experimentKey: string, params = {}) {
    const searchQuery = queryString.stringify(
      {
        experimentKey,
        ...params
      },
      {
        arrayFormat: 'comma',
        skipEmptyString: true,
        skipNull: true
      }
    );

    return `${BASE_API_URL}experiment/redirect?${searchQuery}`;
  },
  getMetricNamesForExperiments(experimentKeys: string[], signal?: AbortSignal) {
    return api.post<ExperimentMetricNamesResponse>(
      'query/experiment-metrics',
      {
        experimentKeys
      },
      { signal }
    );
  },
  getImageNamesForExperiments(experimentKeys: string[], signal?: AbortSignal) {
    return api.post<ExperimentImageNamesResponse>(
      'image/image-names',
      {
        experimentKeys
      },
      { signal }
    );
  },
  getDataframeNamesForExperiments(experiments: string[], signal?: AbortSignal) {
    return api.post<string[]>(
      'asset/filter-name-by?type=CSV',
      {
        experiments
      },
      { signal }
    );
  },
  getCurveNamesForExperiments(experimentKeys: string[], signal?: AbortSignal) {
    return api.post<ExperimentCurveNamesResponse>(
      'asset/curve-names',
      {
        experimentKeys
      },
      { signal }
    );
  },

  fetchExperimentPanelsAvailability(experimentKeys: string[]) {
    return api.post<ExperimentPanelsAvailabilityResponse>(
      `experiment/tabs/availability`,
      {
        experimentKeys
      }
    );
  }
};

export default experimentApi;
